import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Container,
  Typography,
  useTheme,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import { useAuth } from "@context/auth";
import { useMatches, useLocation } from "react-router-dom";

import NavigationButtonCst from "@components/NavigationButtonCst";
import { theme } from "src/theme";
import { useTranslation } from "react-i18next";
import { log } from "console";

const V2_SEGMENTS_TO_SKIP = new Set(["v2", "detail"]);
const V2_SEGMENTS_REDIRECTED_TO_CATALOG = new Set([
  "activities",
  "courses",
  "paths",
]);

const Breadcrumb: React.FC<{ crumbs?: { to: string; text: string }[] }> = ({
  crumbs,
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const location = useLocation();
  const { t } = useTranslation(["breadcrumb"]);

  let newCrumbs = location.pathname.split("/").filter((crumb) => crumb !== "");

  const isV2 = location.pathname.startsWith("/v2/");
  const v2CorporateId =
    isV2 && newCrumbs[1] === "corporate" ? newCrumbs[2] : undefined;
  const v2CatalogPath = v2CorporateId
    ? `/v2/corporate/${v2CorporateId}/detail/catalog`
    : "";

  const [scrollClass, setScrollClass] = useState("");
  let lastScrollTop = 0;

  const handleWheel = (e: any) => {
    var actualScrollTop =
      window.pageYOffset || document.documentElement.scrollTop;
    if (actualScrollTop > lastScrollTop) {
      actualScrollTop > 100
        ? setScrollClass("fixed-under")
        : setScrollClass("");
    } else if (actualScrollTop < lastScrollTop) {
      actualScrollTop > 100 ? setScrollClass("fixed-top") : setScrollClass("");
    }
    lastScrollTop = actualScrollTop <= 0 ? 0 : actualScrollTop;
  };

  useEffect(() => {
    window.addEventListener("scroll", (e: any) => {
      handleWheel(e);
    });
  }, []);

  let wordsToRemoveFromBreadCrumb = [
    "SYNC",
    "ASYNC",
    "BLENDED",
    "DA_SCORM",
    "DA_XAPI",
    "CUSTOM",
    "null",
    "DA_PHYSICAL_CLASS",
    "DA_VIRTUAL_CLASS",
    "DA_EXTERNAL_RES",
    "DA_YOUTUBE",
    "DA_LINKEDIN",
    "DA_LTI",
    "DA_SURVEY",
    "DA_URL_RES",
    "COURSE",
    "PATH",
  ];

  const renderItem = useMemo(() => {
    let currentLink = "";
    return newCrumbs.map((crumb, index) => {
      currentLink += `/${crumb}`;

      // If user is not Product Owner it is rendered Corporate Menu
      // don't show corporates table link inside Breadcrumb
      if (
        user &&
        // @ts-ignore
        !user.roles?.includes("PRODUCT_OWNER") &&
        crumb === "corporate"
      ) {
        return undefined;
      }

      if (Number(crumb) > 0) {
        return;
      }

      if (wordsToRemoveFromBreadCrumb.includes(crumb)) {
        return;
      }

      if (isV2 && V2_SEGMENTS_TO_SKIP.has(crumb)) {
        return;
      }

      const isLast = index === newCrumbs.length - 1;

      const v2LabelKey =
        isV2 && crumb === "catalog" ? "offerta-formativa" : crumb;
      const linkOverride =
        isV2 && V2_SEGMENTS_REDIRECTED_TO_CATALOG.has(crumb)
          ? `${v2CatalogPath}?tab=${crumb}`
          : undefined;

      if (isLast || crumb === "edit") {
        return (
          <Typography key={index} variant="supportingTextL">
            {t(v2LabelKey)}
          </Typography>
        );
      }

      return (
        <NavigationButtonCst key={index} to={linkOverride ?? currentLink}>
          <Typography
            variant="supportingTextL"
            sx={{ textTransform: "capitalize" }}
          >
            {t(v2LabelKey)}
          </Typography>
        </NavigationButtonCst>
      );
    });
  }, [newCrumbs, isV2, v2CatalogPath, t, user]);

  return (
    <Box
      sx={{
        p: "20px 0",
        position: "sticky",
        top: "50px",
        transition: "top 0.7s ease ",
        "&.fixed-top, &.fixed-under": {
          zIndex: "99",
          backgroundColor: theme.palette.background.paper,
          borderTop: "0.5px solid rgba(55, 55, 55, 0.4)",
          transition: "top 0.7s ease ",
        },
        "&.fixed-top": {
          top: "50px",
        },
        "&.fixed-under": {
          top: "90px",
        },
      }}
      className={scrollClass === "" ? "" : scrollClass}
    >
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <Breadcrumbs
          id="corporate-breadcrumb-container"
          data-testid="corporate-detail-breadcrumb-navigation"
          maxItems={5}
          itemsAfterCollapse={2}
          aria-label={t("navigationAriaLabel")}
        >
          <NavigationButtonCst to={"/"}>
            <Typography
              variant="supportingTextL"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <HomeIcon sx={{ mr: 0.5 }} />
              {t("home")}
            </Typography>
          </NavigationButtonCst>
          {renderItem}
        </Breadcrumbs>
      </Container>
    </Box>
  );
};

export default Breadcrumb;
